import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ClipCard from "@/components/feature/ClipCard";
import { categories, clips } from "@/mocks/clips";

const SITE_URL = "https://wqxtv.cn";
const PAGE_SIZE = 6;

type SortKey = "latest" | "hot" | "views";

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "latest", label: "最新归档", icon: "ri-time-line" },
  { key: "hot", label: "讨论最热", icon: "ri-fire-line" },
  { key: "views", label: "回看最多", icon: "ri-play-circle-line" },
];

export default function ClipList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get("cat") ?? "all";
  const sort = (searchParams.get("sort") as SortKey) ?? "latest";
  const keyword = searchParams.get("q") ?? "";
  const [visible, setVisible] = useState(PAGE_SIZE);

  const activeCategory = categories.find((item) => item.id === cat) ?? categories[0];

  const filtered = useMemo(() => {
    let list = [...clips];
    if (cat !== "all") {
      list = list.filter((clip) => clip.categoryId === cat);
    }
    if (keyword.trim()) {
      const q = keyword.trim().toLowerCase();
      list = list.filter(
        (clip) =>
          clip.title.toLowerCase().includes(q) ||
          clip.host.toLowerCase().includes(q) ||
          clip.summary.toLowerCase().includes(q) ||
          clip.tags.some((tag) => tag.toLowerCase().includes(q))
      );
    }
    if (sort === "hot") {
      list.sort((a, b) => b.hot - a.hot);
    } else if (sort === "views") {
      list.sort((a, b) => b.views - a.views);
    } else {
      list.sort((a, b) => b.date.localeCompare(a.date));
    }
    return list;
  }, [cat, sort, keyword]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [cat, sort, keyword]);

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === "") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next);
  };

  const shown = filtered.slice(0, visible);
  const canonical = `${SITE_URL}/clips${cat !== "all" ? `?cat=${cat}` : ""}`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "爆料片段库",
      url: canonical,
      inLanguage: "zh-CN",
      description: "按分类与热度整理的爆料直播片段库，收录主播关键发言、弹幕热议与高能时刻剪辑。",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "爆料片段库", item: `${SITE_URL}/clips` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "爆料片段列表",
      itemListElement: filtered.slice(0, 10).map((clip, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: clip.title,
        url: `${SITE_URL}/clip/${clip.id}`,
      })),
    },
  ];

  return (
    <>
      <Seo
        title="爆料片段库 | 爆料直播高能时刻剪辑与文字版合集"
        description="按主题分类浏览爆料直播片段，支持关键词搜索与热度排序，每一段都附带主播关键发言、弹幕热议与直播内容文字版。"
        keywords="爆料片段库,爆料直播,高能时刻剪辑,主播关键发言,直播弹幕热议"
        canonical={canonical}
        jsonLd={jsonLd}
      />

      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "爆料片段库" }]} />

      <section className="mx-auto w-full max-w-content px-4 pt-10 md:px-6 md:pt-14">
        <span className="inline-flex items-center gap-2 text-xs font-label tracking-[0.2em] text-primary-600 uppercase">
          <span className="inline-block h-px w-6 bg-primary-500" />
          Archive
        </span>
        <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          爆料片段库
        </h1>
        <p className="mt-4 max-w-3xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
          这里收录了爆料直播里值得回看的片段。你可以按主题分类浏览，也可以直接搜索主播名字或关键词，
          每个片段都配有主播关键发言、弹幕热议和直播内容文字版。
        </p>

        <form
          className="mt-8 flex flex-col gap-3 sm:flex-row"
          onSubmit={(event) => {
            event.preventDefault();
            const input = (event.currentTarget.elements.namedItem("keyword") as HTMLInputElement) ?? null;
            updateParams({ q: input?.value.trim() || null });
          }}
          role="search"
        >
          <label className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-3 focus-within:border-primary-400">
            <i className="ri-search-line text-foreground-500" />
            <input
              type="search"
              name="keyword"
              defaultValue={keyword}
              placeholder="搜索片段标题、主播或标签"
              className="w-full bg-transparent text-sm font-label text-foreground-900 outline-none placeholder:text-foreground-500"
            />
          </label>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-2-line" />
            搜索片段
          </button>
        </form>
      </section>

      <section className="mx-auto w-full max-w-content px-4 py-8 md:px-6">
        <div className="flex flex-wrap gap-2">
          {categories.map((item) => {
            const active = item.id === cat;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => updateParams({ cat: item.id === "all" ? null : item.id })}
                className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-label transition-colors ${
                  active
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300"
                }`}
              >
                <i className={item.icon} />
                {item.name}
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex flex-col gap-4 border-y border-background-200 py-4 md:flex-row md:items-center md:justify-between">
          <p className="font-label text-sm text-foreground-600">
            当前分类：<span className="text-foreground-950">{activeCategory.name}</span>
            <span className="mx-2 text-foreground-300">/</span>
            共 <span className="text-primary-600">{filtered.length}</span> 个片段
            {keyword && (
              <>
                <span className="mx-2 text-foreground-300">/</span>
                关键词 “{keyword}”
              </>
            )}
          </p>
          <div className="flex flex-wrap gap-2">
            {sortOptions.map((option) => {
              const active = option.key === sort;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => updateParams({ sort: option.key === "latest" ? null : option.key })}
                  className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-label transition-colors ${
                    active
                      ? "border-secondary-500 bg-secondary-100 text-secondary-900"
                      : "border-background-300 text-foreground-600 hover:border-secondary-300"
                  }`}
                >
                  <i className={option.icon} />
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-content px-4 pb-14 md:px-6">
        {shown.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((clip) => (
                <ClipCard key={clip.id} clip={clip} />
              ))}
            </div>

            {visible < filtered.length && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-label text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-arrow-down-circle-line" />
                  加载更多片段（剩余 {filtered.length - visible}）
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-600">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-950">暂时没有匹配的片段</h2>
            <p className="max-w-md font-body text-sm text-foreground-600">
              换个关键词试试，或者回到全部分类浏览最新归档的爆料片段。
            </p>
            <button
              type="button"
              onClick={() => setSearchParams(new URLSearchParams())}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line" />
              重置筛选条件
            </button>
          </div>
        )}

        <div className="mt-12 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-5">
          <span className="font-label text-sm text-foreground-600">热门标签</span>
          {["砍价翻车", "现场失控", "五杀被抢", "绝杀被吹", "本尊回应", "弹幕叫停"].map((tag) => (
            <Link
              key={tag}
              to={`/clips?q=${encodeURIComponent(tag)}`}
              className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-label text-secondary-900 transition-colors hover:bg-secondary-200"
            >
              #{tag}
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}